<template>
  <!-- Logged-in but not yet a tenant (or deleted): explain and offer sign-out only -->
  <div v-if="showRestrictedPortal" class="space-y-6">
    <div class="p-4 bg-amber-50 border border-amber-200 rounded-lg">
      <h3 class="text-sm font-semibold text-amber-900 mb-2">
        {{ restrictedTitle }}
      </h3>
      <p class="text-sm text-amber-900/90 whitespace-pre-line">
        {{ restrictedBody }}
      </p>
    </div>
    <button
      type="button"
      :disabled="signingOut"
      @click="handleRestrictedLogout"
      class="w-full px-4 py-3 bg-gray-100 text-gray-900 font-medium rounded-lg border border-gray-200 hover:bg-gray-200 focus:ring-4 focus:ring-gray-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {{ signingOut ? $t('auth.signingOut') : $t('auth.logout') }}
    </button>
  </div>

  <form v-else @submit.prevent="handleSubmit" class="space-y-6">
    <!-- Login Field -->
    <div>
      <label for="login" class="block text-sm font-medium text-gray-700 mb-2">
        {{ $t('auth.login') }}
      </label>
      <input
        id="login"
        v-model="form.login"
        type="text"
        required
        autocomplete="username"
        :disabled="isLoading"
        :minlength="AUTH_LOGIN_MIN_LENGTH"
        :maxlength="AUTH_LOGIN_MAX_LENGTH"
        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition disabled:bg-gray-100 disabled:cursor-not-allowed"
        :placeholder="$t('auth.loginPlaceholder')"
      />
    </div>

    <!-- Password Field -->
    <div>
      <label for="password" class="block text-sm font-medium text-gray-700 mb-2">
        {{ $t('auth.password') }}
      </label>
      <div class="relative">
        <input
          id="password"
          v-model="form.password"
          :type="showPassword ? 'text' : 'password'"
          required
          autocomplete="current-password"
          :disabled="isLoading"
          :minlength="AUTH_PASSWORD_MIN_LENGTH"
          :maxlength="AUTH_PASSWORD_MAX_LENGTH"
          class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition disabled:bg-gray-100 disabled:cursor-not-allowed pr-12"
          :placeholder="$t('auth.passwordPlaceholder')"
        />
        <button
          type="button"
          @click="showPassword = !showPassword"
          class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
        >
          <svg v-if="showPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
          </svg>
          <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Error Message -->
    <div v-if="error" class="p-4 bg-red-50 border border-red-200 rounded-lg">
      <div class="flex items-center gap-2 text-red-700">
        <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span class="text-sm font-medium">{{ error }}</span>
      </div>
    </div>

    <!-- Submit Button -->
    <div class="flex justify-center">
      <button
        type="submit"
        :disabled="isLoading || !isFormValid"
        class="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 transition disabled:bg-blue-400 disabled:cursor-not-allowed"
      >
        <svg v-if="isLoading" class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
        </svg>
        {{ isLoading ? $t('auth.loggingIn') : $t('auth.loginButton') }}
      </button>
    </div>
  </form>
</template>

<script setup>
import { ref, reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { authStore } from '../../stores/auth';
import { spaLocaleHomePath } from '../../utils/spaLocaleHomePath';
import {
  AUTH_LOGIN_MIN_LENGTH,
  AUTH_LOGIN_MAX_LENGTH
} from '@merlot/common/authLoginDefaults.js';
import {
  AUTH_PASSWORD_MIN_LENGTH,
  AUTH_PASSWORD_MAX_LENGTH
} from '@merlot/common/authPasswordDefaults.js';
import { isLoginFormValid } from '@merlot/common/isLoginFormValid.js';
import { resetAdminSetupWizardSession } from '../../composables/useAdminSetupChecklist.js';

const emit = defineEmits(['success', 'close']);

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const form = reactive({
  login: '',
  password: ''
});

const showPassword = ref(false);
const error = ref(null);
const signingOut = ref(false);
const isLoading = computed(() => authStore.isLoading);

/** Active session without tenant/admin portal access */
const showRestrictedPortal = computed(() => {
  const r = authStore.user?.role;
  return !!(authStore.isAuthenticated && (r === 'pending' || r === 'deleted' || r === 'violated'));
});

const restrictedTitle = computed(() => {
  const r = authStore.user?.role;
  if (r === 'violated') return t('auth.accountViolatedTitle');
  if (r === 'deleted') return t('auth.accountDeletedTitle');
  return t('auth.pendingApprovalTitle');
});

const restrictedBody = computed(() => {
  const r = authStore.user?.role;
  if (r === 'violated') return t('auth.accountViolatedBody');
  if (r === 'deleted') return t('auth.accountDeletedBody');
  return t('auth.pendingApprovalBody');
});

const isFormValid = computed(() => isLoginFormValid(form.login, form.password));

async function handleRestrictedLogout() {
  signingOut.value = true;
  try {
    await authStore.logout();
    form.login = '';
    form.password = '';
    error.value = null;
  } finally {
    signingOut.value = false;
  }
}

async function handleSubmit() {
  error.value = null;

  if (!isFormValid.value) return;

  const result = await authStore.login(form.login.trim(), form.password);
  
  if (result.success) {
    const role = result.user?.role;
    if (role === 'pending' || role === 'deleted' || role === 'violated') {
      emit('success', result.user);
      return;
    }

    emit('success', result.user);

    if (authStore.isAdmin) {
      resetAdminSetupWizardSession();
    }
    
    // Check for return URL query parameter
    const returnUrl = route.query.return;
    
    if (returnUrl && typeof returnUrl === 'string') {
      // Redirect to the original route user was trying to access
      try {
        const decodedUrl = decodeURIComponent(returnUrl);
        // Validate that it's a safe internal route
        if (decodedUrl.startsWith('/') && !decodedUrl.startsWith('//')) {
          if (
            (decodedUrl === '/dashboard' || decodedUrl.startsWith('/dashboard/')) &&
            !authStore.isTenant
          ) {
            router.push(spaLocaleHomePath());
            return;
          }
          router.push(decodedUrl);
          return;
        }
      } catch (e) {
        console.warn('Invalid return URL:', e);
      }
    }
    
    // Default redirect based on role
    if (authStore.isAdmin) {
      router.push('/admin');
    } else {
      router.push('/dashboard');
    }
  } else {
    // Map error messages to i18n keys
    if (result.error.includes('locked')) {
      error.value = t('auth.accountLocked');
    } else if (result.error.includes('deactivated') || result.error.includes('inactive')) {
      error.value = t('auth.accountDeactivated');
    } else {
      error.value = t('auth.loginError');
    }
  }
}
</script>

